import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditTrailApi } from '../../api/auditTrail';
import { formatDate } from '../../utils/formatDate';
import DataTable from '../../components/DataTable';
import DatePickerInput from '../../components/DatePickerInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import { monthsAgo, today } from '../../utils/formatDate';
import { useReportScreen } from '../../components/ScreenContext';

const ENTITY_TYPES = ['JournalEntry', 'InvoiceMade', 'InvoiceReceived', 'BankOperation', 'Offset', 'AdvanceReport', 'FixedAsset', 'PayrollRecord', 'TaxRate', 'Company'];

export default function AuditTrail() {
  const [from, setFrom] = useState(monthsAgo(1));
  const [to, setTo] = useState(today());
  const [entityType, setEntityType] = useState('');
  const [limit, setLimit] = useState(100);

  const { data: entries, isLoading } = useQuery({
    queryKey: ['audit-trail', from, to, entityType, limit],
    queryFn: () => auditTrailApi.list({
      from, to,
      entityType: entityType || undefined,
      limit,
    }),
  });

  const totalCount = entries?.length ?? 0;

  useReportScreen(`Audit Trail (${totalCount} entries)`, {
    recordCount: totalCount,
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">Audit Trail</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <DatePickerInput label="From" value={from} onChange={setFrom} inputClassName="w-36" />
        <DatePickerInput label="To" value={to} onChange={setTo} inputClassName="w-36" />
        <div>
          <label className="block text-xs text-gray-500 mb-1">Entity Type</label>
          <select className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            <option value="">All</option>
            {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Limit</label>
          <select className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={limit} onChange={(e) => setLimit(parseInt(e.target.value))}>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={[
            {
              key: 'timestamp',
              header: 'Time',
              render: (row) => formatDate(row.changedAt),
              className: 'whitespace-nowrap text-xs',
            },
            {
              key: 'action',
              header: 'Action',
              render: (row) => (
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  row.action === 'Create' ? 'bg-green-100 text-green-800' :
                  row.action === 'Update' ? 'bg-primary-100 text-primary-800' :
                  row.action === 'Delete' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>{row.action}</span>
              ),
            },
            {
              key: 'entityType',
              header: 'Entity',
              render: (row) => <span className="text-xs font-mono">{row.entityType}</span>,
            },
            {
              key: 'entityId',
              header: 'ID',
              render: (row) => <span className="text-xs num">#{row.entityId}</span>,
              className: 'text-right',
            },
            {
              key: 'changedBy',
              header: 'User',
              render: (row) => <span className="text-xs">{row.changedBy || 'system'}</span>,
            },
            {
              key: 'changes',
              header: 'Changes',
              render: (row) => (
                <span className="text-xs text-gray-500 max-w-xs truncate block" title={row.changes}>
                  {row.changes?.substring(0, 100)}{row.changes?.length > 100 ? '...' : ''}
                </span>
              ),
              className: 'max-w-xs',
            },
          ]}
          data={entries ?? []}
          keyFn={(row) => row.id}
          emptyMessage="No audit entries found for the selected filters"
        />
      </div>
    </div>
  );
}
